import React, { Component } from 'react';
import LoadFailImage from './load-fail-image';
import ImageUrl from '../../slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { convertSuffix } from '@src/slide/application/service/img-loader-manager/core/constant';

interface LZImgProps {
    xlinkHref: string;
    width: number;
    height: number;
    transform?: string;
    preserveAspectRatio?: string;
    x?: number;
    y?: number;
    playGif?: boolean;
    syncLoad?: boolean;
    style?: React.CSSProperties;
    original?: boolean;
    onImgLoaded?: (width: number, height: number) => void;
    cursor?: string;
    className?: string;
    filter?: string;
}

interface LZImgState {
    imageLoadFail: boolean;
}

class LZImg extends Component<LZImgProps, LZImgState> {
    private elementRefs: any = null;
    public constructor(props: LZImgProps) {
        super(props);
        this.state = {
            imageLoadFail: false,
        };
    }

    public componentDidMount() {
        this.elementRefs.callback = this.onImgLoaded;
        this.elementRefs.onLoadError = this.onImgLoadError;
        this.getAppServices().viewableServiceLayer.imgLoaderManager.addImage(this.elementRefs);
    }

    public componentWillUnmount() {
        this.elementRefs.callback = null;
        this.elementRefs.onLoadError = null;
        this.getAppServices().viewableServiceLayer.imgLoaderManager.removeImage(this.elementRefs);
    }

    public shouldComponentUpdate(nextProps: LZImgProps, nextState: LZImgState): boolean {
        const needRender = this.props.xlinkHref !== nextProps.xlinkHref
            || this.props.width !== nextProps.width
            || this.props.height !== nextProps.height
            || this.props.x !== nextProps.x
            || this.props.y !== nextProps.y
            || this.props.filter !== nextProps.filter
            || (nextState.imageLoadFail && this.state.imageLoadFail !== nextState.imageLoadFail);

        if (this.props.xlinkHref !== nextProps.xlinkHref) {
            this.getAppServices().viewableServiceLayer.imgLoaderManager.removeImage(this.elementRefs);
        }

        return needRender;
    }

    public componentDidUpdate() {
        if (this.state.imageLoadFail) return;
        this.elementRefs.callback = this.onImgLoaded;
        this.elementRefs.onLoadError = this.onImgLoadError;
        this.getAppServices().viewableServiceLayer.imgLoaderManager.addImage(this.elementRefs);
    }
    public render() {
        const { href, dataSrc, dataOriginal } = this.getRenderInfo();
        const style = this.getStyle();

        return (
            <React.Fragment>
                {this.state.imageLoadFail && (
                    <LoadFailImage
                        width={this.props.width}
                        height={this.props.height}
                    />
                )}
                <image
                    // 离线编辑时，桌面端的service worker在没有origin的情况下，访问图片缓存会报500
                    crossOrigin={'anonymous'}
                    xlinkHref={href}
                    data-src={this.state.imageLoadFail ? '' : dataSrc}
                    filter={this.props.filter && `url(#${this.props.filter})`}
                    width={this.props.width}
                    height={this.props.height}
                    // shouldComponentUpdate已经有优化了，这里可以强制刷新。
                    key={this.props.xlinkHref} // 加key，是为了强制触发dom刷新，否则rect优化后不会刷新非UI变动的数据。
                    transform={this.props.transform}
                    preserveAspectRatio={this.props.preserveAspectRatio}
                    x={this.props.x}
                    y={this.props.y}
                    cursor={this.props.cursor}
                    className={this.props.className ? `${this.props.className} lzImage` : 'lzImage'}
                    ref={(el) => {
                        this.elementRefs = el;
                    }}
                    data-original={dataOriginal}
                    style={style}
                ></image>
            </React.Fragment>
        );
    }

    private onImgLoaded = (width: number, height: number) => {
        if (this.props.onImgLoaded) {
            this.props.onImgLoaded(width, height);
        }
    };

    private onImgLoadError = () => {
        this.setState({
            imageLoadFail: true,
        });
    };

    /**
     * 检查同步还是异步渲染
     * @returns {{xlinkHref: *, syncLoad: *, thumbHref: *}}
     */
    private checkNeedSyncLoad(): { xlinkHref: string; thumbHref: string | null; syncLoad: boolean } {
        const { imgLoaderManager } = this.getAppServices().viewableServiceLayer;
        let { xlinkHref } = this.props;
        if (ImageUrl.isBlobUrl(xlinkHref)) {
            xlinkHref = ImageUrl.stripAllTags(xlinkHref);
            return {
                xlinkHref,
                thumbHref: xlinkHref,
                syncLoad: true,
            };
        }

        let thumbHref: string | null = null;
        let syncLoad = false;

        if (imgLoaderManager.getUrlParam(xlinkHref, '_type') === 'gif') {
            xlinkHref = `${xlinkHref}&scene=player`;
        } else if (this.props.syncLoad) {
            if (this.props.original) {
                if (imgLoaderManager.checkIsLoaded(xlinkHref)) {
                    syncLoad = true;
                }
            } else {
                thumbHref = imgLoaderManager.getThumb1600(xlinkHref);
                if (imgLoaderManager.checkIsLoaded(thumbHref)) {
                    syncLoad = true;
                }
            }
        }

        if (imgLoaderManager.convertUrlSet.has(xlinkHref)) {
            xlinkHref = `${xlinkHref}${convertSuffix}`;
            thumbHref = `${thumbHref}${convertSuffix}`;
        }

        return { xlinkHref, thumbHref, syncLoad };
    }

    /**
     * 获取不同状态的渲染信息
     * @returns {{dataOriginal: *, href: *, dataSrc: *}}
     */
    private getRenderInfo = (): { href: string; dataSrc?: string; dataOriginal?: number } => {
        const { original } = this.props;
        const { xlinkHref, thumbHref, syncLoad } = this.checkNeedSyncLoad();
        let href;
        let dataSrc;
        let dataOriginal;
        if (syncLoad) {
            href = original ? xlinkHref : thumbHref;
        } else {
            dataSrc = xlinkHref;
            dataOriginal = original ? 1 : undefined;
        }
        return { href, dataSrc, dataOriginal };
    };

    private getStyle(): React.CSSProperties {
        const displayStyles = this.state.imageLoadFail ? { display: 'none' } : {};

        return {
            ...this.props.style,
            ...displayStyles,
            pointerEvents: 'none',
        };
    }

    private getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }
}

export default LZImg;
